import './app.scss';
import domready from 'domready';
import 'lazysizes';
import { getOffset, smoothScrollTo } from './utilities/js/helper';
import { loadLazyImages } from './utilities/js/lazy-image-loader';
import { bindTrackingEvent, getCtaTrackingData, sendTracking } from './utilities/js/tracking';
import { EventScroller } from './utilities/js/event-scroller';
import { MediaMatchHandler } from './utilities/js/media-match-handler';
import { ResizeHandler } from './utilities/js/resize-handler';
import { Accessibility } from './layout/accessibility/accessibility';
import AdjustSmartBanner from '@adjustcom/smart-banner-sdk';

window.eventScroller = new EventScroller();
window.mediaMatchHandler = new MediaMatchHandler();
window.resizeHandler = new ResizeHandler();

window.youtubeApi = typeof window.YT !== 'undefined';
window.youtubeApiLoading = false;
window.locale = document.querySelector('html').getAttribute('lang');
window.siteheaderAPI = null;

window.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
window.reducedMotionActive = window.reducedMotion.matches;

window.oneTrustInitialized = false;
window.enabledCookies = [];

// GLOBAL OPTIONS
window.OPTIONS = {
    breakpoints: {
        s: 320,
        m: 768,
        l: 1024,
        xl: 1280,
        xxl: 1440,
        xxxl: 1920
    },
    loader: '<span class="loader"><span></span><span></span><span></span></span>',
    speed: 500
};

const waitForOneTrust = (maxRetries = 50, interval = 100) => {
    let retries = 0;

    const checkInterval = setInterval(() => {
        if (typeof OneTrust !== 'undefined') {
            const initEvent = new CustomEvent('cookieConsentInitialized');
            window.oneTrustInitialized = true;
            window.enabledCookies = OnetrustActiveGroups.split(',');
            window.dispatchEvent(initEvent);

            const handleConsentChange = () => {
                const updateEvent = new CustomEvent('cookieConsentChanged');
                window.enabledCookies = OnetrustActiveGroups.split(',');
                window.dispatchEvent(updateEvent);
            };

            OneTrust.OnConsentChanged(handleConsentChange);

            clearInterval(checkInterval);
        } else {
            retries++;
            if (retries >= maxRetries) {
                clearInterval(checkInterval);
            }
        }
    }, interval);
};

const reduceMotionCheck = () => {
    window.reducedMotion.addEventListener('change', (event) => {
        // Check if the reduced motion preference has changed
        if (window.reducedMotionActive !== event.matches) {
            // Update the reduced motion media query and the active state
            window.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
            window.reducedMotionActive = window.reducedMotion.matches;

            // Create a custom event to notify the change in reduced motion preference
            const motionEvent = new CustomEvent('motionPreferenceChanged', {
                detail: {
                    prefersReducedMotion: window.reducedMotionActive
                }
            });

            // Dispatch the custom event to notify other parts of the application
            window.dispatchEvent(motionEvent);

            // Necessary to start/reset scroll animation (scroll 1px up and down to trigger smooth scrolling)
            const currentScrollPosition = window.scrollY;

            window.scrollTo({
                top: currentScrollPosition + 1,
                behavior: 'smooth'
            });

            window.scrollTo({
                top: currentScrollPosition - 1,
                behavior: 'smooth'
            });
        }
    });
};

const browserCheck = () => {
    window.browser = {
        csshover: !window.matchMedia('(pointer:coarse)').matches,
        ios: (/iPhone|iPad|iPod/i).test(navigator.userAgent),
        iphone: (/iPhone|iPod/i).test(navigator.userAgent),
        android: (/(android)/i).test(navigator.userAgent),
        firefox: (/firefox/i).test(navigator.userAgent),
        safari: (/Version\/[\d.]+.*Safari/).test(navigator.userAgent)
    };

    Object.entries(window.browser).forEach(([key, value]) => {
        document.documentElement.classList.add((!value) ? `no-${key}` : `${key}`);
    });
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
};

window.checkInView = (entries, observer) => {
    entries.forEach((entry) => {
        if (entry.isIntersecting) {
            if (!entry.target.classList.contains('inview')) {
                entry.target.classList.add('inview');

                if (typeof entry.target.dataset.footer === 'undefined') {
                    window.inViewObserver.unobserve(entry.target);
                }
            }
        }
    });
};

window.inViewObserver = new IntersectionObserver(window.checkInView, {
    rootMargin: '-15%'
});

const initContent = ($content) => {
    const $$anchor = $content.querySelectorAll('a[href*="#"]:not([data-anchor-navigation])');

    for (let i = 0; i < $$anchor.length; i++) {
        const $anchor = $$anchor[i];
        let anchorTimeout = null;

        $anchor.addEventListener('click', (e) => {
            let baseUrl = $anchor.getAttribute('href').split('#')[0];
            const targetId = $anchor.getAttribute('href').split('#')[1];
            const $target = document.getElementById(targetId);
            const currentUrl = window.location.href;
            let focusTimer = null;

            if (baseUrl === '') {
                baseUrl = currentUrl;
            }

            if (baseUrl === currentUrl && $target !== null) {
                const anchorDimensions = getOffset($target);
                const anchorDimensionsTop = anchorDimensions.top;

                smoothScrollTo(0, anchorDimensionsTop, window.OPTIONS.speed);

                $target.setAttribute('tabindex', 0);
                $target.focus();

                focusTimer = setTimeout(() => {
                    $target.removeAttribute('tabindex');
                    clearTimeout(focusTimer);
                }, 200);

                if (window.location.hash && window.siteheaderAPI) {
                    clearTimeout(anchorTimeout);
                    anchorTimeout = window.setTimeout(() => {
                        window.siteheaderAPI.toggleHeader(false);
                    }, 500);
                }
                e.preventDefault();
            }
        });

        if (window.location.hash && window.siteheaderAPI) {
            clearTimeout(anchorTimeout);
            anchorTimeout = window.setTimeout(() => {
                window.siteheaderAPI.toggleHeader(false);
            }, 500);
        }
    }

    const $$trackableCtas = $content.querySelectorAll('.event-tracking');

    $$trackableCtas.forEach(($trackableCta) => {
        const trackingData = getCtaTrackingData($trackableCta);

        $trackableCta.addEventListener('click', () => {
            sendTracking(trackingData);
        });
    });

    loadLazyImages();
    bindTrackingEvent($content);
};

domready(function () {
    const initApplication = () => {
        browserCheck();
        reduceMotionCheck();
        waitForOneTrust();

        window.eventScroller.init();
        window.resizeHandler.init();

        window.dataInview = document.querySelectorAll('[data-inview]:not(.inview)');
        setTimeout(() => {
            window.dataInview.forEach((dataInView) => {
                window.inViewObserver.observe(dataInView);
            });
        }, 500);

        // Accessibility
        const accessibilityAPI = new Accessibility();
        accessibilityAPI.initialize();

        const contentUpdateEvent = new CustomEvent('content.loaded', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: document
            }
        });

        window.dispatchEvent(contentUpdateEvent);

        document.body.classList.add('page--loaded');
        window.addEventListener('beforeunload', () => {

            const isMailTo = document.activeElement.protocol === 'mailto:';
            const isTel = document.activeElement.protocol === 'tel:';

            if (!isMailTo && !isTel) {
                document.body.classList.remove('page--loaded');
            }
        });

        window.addEventListener('pageshow', (event) => {
            const historyTraversal = event.persisted || (typeof window.performance !== 'undefined' && window.performance.navigation.type === 2);
            if (historyTraversal) {
                window.location.reload();
            }
        });

        // jump to main content and set focus on container
        document.querySelector('.hidden-shortcut a[href="#main"]')?.addEventListener('click', () => {
            document.querySelector('#main').focus();
        });


        const $$cookieConsentOverlayOpenerLinks = document.querySelectorAll('a[href="cookie-consent"]');

        /**
         * Replaces each cookie consent anchor link with a semantic button element
         * so the cookie settings overlay can be triggered as an action instead of navigation.
         *
         * @param {HTMLAnchorElement} $link - The original anchor element to convert into a button.
         * @returns {void} Does not return a value.
         */
        $$cookieConsentOverlayOpenerLinks.forEach($link => {
            const $button = document.createElement('button');
            $button.textContent = $link.textContent;

            if ($link.classList.length > 0) {
                $button.className = $link.className;
            }

            /**
             * Copies only the relevant attributes from the original link to the replacement
             * button to preserve accessibility metadata and selector hooks.
             *
             * @param {Attr} attr - The current attribute from the original link element.
             * @returns {void} Does not return a value.
             */
            Array.from($link.attributes).forEach(attr => {
                // Preserve only the attributes required for identification, accessibility,
                // or cookie consent behavior on the generated button.
                if (attr.name === 'data-cookieconsent' || ['id', 'title', 'aria-label'].includes(attr.name)) {
                    $button.setAttribute(attr.name, attr.value);
                }
            });

            $button.setAttribute('data-cookieconsent', '');
            $button.setAttribute('type', 'button');

            // Replace the link with a button so the DOM reflects the actual UI behavior.
            $link.parentNode.replaceChild($button, $link);
        });

        const $$cookieConsentOverlayOpener = document.querySelectorAll('[data-cookieconsent]');

        /**
         * Attaches a click handler to each cookie consent trigger element so the OneTrust
         * cookie settings overlay can be opened on user interaction.
         *
         * @param {HTMLElement} $button - The element that should open the cookie consent overlay when clicked.
         * @returns {void} Does not return a value.
         */
        $$cookieConsentOverlayOpener.forEach($button => {
            /**
             * Opens the OneTrust cookie settings overlay when the trigger element is clicked.
             *
             * @param {MouseEvent} e - The click event triggered by the element.
             * @returns {void} Does not return a value.
             */
            $button.addEventListener('click', e => {
                // Guard against cases where the OneTrust script is not available yet.
                if (typeof OneTrust !== 'undefined') {
                    OneTrust.ToggleInfoDisplay();
                }

                // Prevent default interaction behavior so only the overlay action is executed.
                e.preventDefault();
            });
        });

        initContent(document);

        const $bannerSelector = document.querySelector('[data-smartbanner]');
        if ($bannerSelector) {
            AdjustSmartBanner.init({
                appToken: 'na72bqc48ow0',
                language: 'de',
                logLevel: 'verbose'
            });
        }
    };

    initApplication();
});
